import * as i0 from '@angular/core';
import { NgModule, Injectable } from '@angular/core';

class HeapModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: HeapModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: HeapModule }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: HeapModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: HeapModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [],
                    declarations: [],
                    exports: [],
                    providers: [],
                }]
        }] });

class HeapService {
    getHeap() {
        if (window.heap) {
            return window.heap;
        }
        else {
            return null;
        }
    }
    /**
     * Gets the current user's identity (if set)
     */
    getIdentity() {
        return this.getHeap()?.identity;
    }
    /**
     * This API allows you to attach a unique identity and maintain user histories across sessions, devices, and browsers under a single profile in Heap.
     * https://developers.heap.io/reference/identify
     * @param eventName name of the custom interaction. Limited to 255 characters.
     * @param eventProps a JSON object, NSDictionary, or Map containing key-value pairs to be associated with an event.
     * Keys must be strings, while values may be a number or a string.
     */
    track(eventName, eventProps) {
        this.getHeap()?.track(eventName, eventProps);
    }
    /**
     * Use this API to send stateful information into Heap as a set of global key-value pairs that get attached to all of a user's subsequent events.
     * https://developers.heap.io/reference/addeventproperties
     * @param eventProperties a JSON object, NSDictionary, or Map containing key-value pairs to be associated with every subsequent event.
     * Keys and values must be a number or string fewer than 1024 characters.
     * All properties (keys and values) must be less than 4096 character in total, or they'll be dropped silently.
     */
    addEventProperties(eventProperties) {
        this.getHeap()?.addEventProperties(eventProperties);
    }
    /**
     * Clears all event properties, then adds the new ones provided
     * @param eventProperties
     */
    clearAndAddEventProperties(eventProperties) {
        this.clearEventProperties();
        this.getHeap()?.addEventProperties(eventProperties);
    }
    /**
     * Use this API to stop a single event property from getting attached to all subsequent events. This API is usually called in relation to addEventProperties.
     * https://developers.heap.io/reference/removeeventproperty
     * @param eventPropertyName name of the event property to remove. This stops attaching the property to all subsequent events.
     */
    removeEventProperty(eventPropertyName) {
        this.getHeap()?.removeEventProperty(eventPropertyName);
    }
    /**
     * Removes the event property and then re-adds with the new value provided
     * @param eventPropertyName
     * @param newValue
     */
    removeEventPropertyAndAssignNewValue(eventPropertyName, newValue) {
        this.removeEventProperty(eventPropertyName);
        this.addEventProperties({
            [eventPropertyName]: newValue
        });
    }
    /**
     * This API should be called prior to calling the addEventProperties API.
     * It removes all previously stored event properties from the cookie, which ensures the properties getting added by the next addEventProperties call are always relevant.
     * https://developers.heap.io/reference/cleareventproperties
     */
    clearEventProperties() {
        this.getHeap()?.clearEventProperties();
    }
    /**
     * This API allows you to attach a unique identity and maintain user histories across sessions, devices, and browsers under a single profile in Heap.
     * Do Not Use a Non-Unique Identifier
     * https://developers.heap.io/reference/identify
     * @param identity a string that uniquely identifies a user, such as an email, handle, or username.
     * This means no two users in one environment may share the same identity. Must be fewer than 255 characters.
     */
    identify(identity) {
        this.getHeap()?.identify(identity);
    }
    /**
     * Use this API to reset a user’s identity to an anonymous state after they have logged out. See Using Identify for use cases when managing identity across browsers and devices.
     * https://developers.heap.io/reference/resetidentity
     */
    resetIdentity() {
        this.getHeap()?.resetIdentity();
    }
    /**
     * This API allows you to attach custom properties at the user level, such as user-level info from your database or demographic info.
     * https://developers.heap.io/reference/adduserproperties
     * @param userProperties a JSON object, NSDictionary, or Map containing key-value pairs to be associated with a user.
     * Keys and values must be a number or string, with the value being 255 characters or fewer.
     */
    addUserProperties(userProperties) {
        this.getHeap()?.addUserProperties(userProperties);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: HeapService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: HeapService, providedIn: HeapModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: HeapService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: HeapModule
                }]
        }] });

var TrackingEventNames;
(function (TrackingEventNames) {
    TrackingEventNames["BulkCreateReferenceFields"] = "Bulk create reference fields";
    TrackingEventNames["ContactSupport"] = "Contact Support";
    TrackingEventNames["Error"] = "Error";
    TrackingEventNames["HelpLink"] = "Help Link";
    TrackingEventNames["LiveChat"] = "Live Chat";
    TrackingEventNames["Login"] = "Login";
    TrackingEventNames["NewApplication"] = "New Application";
    TrackingEventNames["OpenHelp"] = "Open Help";
    TrackingEventNames["OrganizationSearch"] = "Organization Search";
    TrackingEventNames["PageHelpModal"] = "Page Help Modal";
    TrackingEventNames["PageView"] = "Page View";
    TrackingEventNames["SaveReferenceField"] = "Save reference field";
    TrackingEventNames["SaveTableOrFieldGroup"] = "Save table or field group";
    TrackingEventNames["ShowTableOrFieldGroupConfig"] = "Show table or field group config";
    TrackingEventNames["SubmitForm"] = "Submit Form";
    TrackingEventNames["WhatsNew"] = "What's New";
})(TrackingEventNames || (TrackingEventNames = {}));

var TrackingPropertyNames;
(function (TrackingPropertyNames) {
    TrackingPropertyNames["ClientId"] = "Client Id";
    TrackingPropertyNames["ClientName"] = "Client Name";
    TrackingPropertyNames["Dismissed"] = "Dismissed";
    TrackingPropertyNames["Email"] = "Email";
    TrackingPropertyNames["FieldName"] = "Field Name";
    TrackingPropertyNames["FieldType"] = "Field Type";
    TrackingPropertyNames["FormId"] = "Form Id";
    TrackingPropertyNames["FormName"] = "Form Name";
    TrackingPropertyNames["IsDefault"] = "Is Default";
    TrackingPropertyNames["IsImpersonated"] = "Is Impersonated";
    TrackingPropertyNames["IsImport"] = "Is Import";
    TrackingPropertyNames["Language"] = "Language";
    TrackingPropertyNames["Link"] = "Link";
    TrackingPropertyNames["Message"] = "Message";
    TrackingPropertyNames["Method"] = "Method";
    TrackingPropertyNames["Name"] = "Name";
    TrackingPropertyNames["ProgramId"] = "Program Id";
    TrackingPropertyNames["ProgramName"] = "Program Name";
    TrackingPropertyNames["RecordCount"] = "Record Count";
    TrackingPropertyNames["SearchTerm"] = "Search Term";
    TrackingPropertyNames["SiteId"] = "Site Id";
    TrackingPropertyNames["Success"] = "Success";
    TrackingPropertyNames["UserType"] = "User Type";
})(TrackingPropertyNames || (TrackingPropertyNames = {}));

/**
 * Generated bundle index. Do not edit.
 */

export { HeapModule, HeapService, TrackingEventNames, TrackingPropertyNames };
